import { readConsentCookie, type AnalyticsConsent } from "./firebase";

/**
 * PostHog (project123 D-001/D-002) — session replay + heatmaps + events.
 *
 * Two lanes (D-002): a cookieless BASE lane (no cookie, no PII, replay OFF) runs
 * always for aggregate events/autocapture/heatmaps — no consent needed, so the
 * product journey is never gated by the banner. The FULL lane (cookie persistence
 * + session replay, which captures on-screen PII) only engages after the visitor
 * grants the "statistics" category (same cookie_consent decision as Firebase
 * Analytics, project100 D-005); revoking opts out and stops session recording.
 *
 * Public env keys (safe in the client bundle):
 *   VITE_POSTHOG_KEY  — required; if missing PostHog stays disabled (WARNING)
 *   VITE_POSTHOG_HOST — default US https://us.i.posthog.com ; use
 *                       https://eu.i.posthog.com for an EU-region project
 */

export const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
export const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://us.i.posthog.com";

type PostHogClient = {
  init: (key: string, options?: Record<string, unknown>) => void;
  opt_in_capturing: () => void;
  opt_out_capturing: (optOut?: boolean) => void;
  startSessionRecording: () => void;
  stopSessionRecording: () => void;
  set_config: (options: Record<string, unknown>) => void;
  identify: (uid: string, props?: Record<string, unknown>) => void;
  reset: () => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
  // PROJECT-289 T0289.9 — the ONE reader of the distinct id, and it exists to link a stored feedback
  // row back to the session recording it came from. Declared optional because this type enumerates
  // the surface the module USES rather than the library's whole API, and `posthogDistinctId` guards
  // with `?.` for the same reason: a client that never loaded must not throw on a read.
  get_distinct_id?: () => string | undefined;
};

let client: PostHogClient | null = null;
let loading: Promise<PostHogClient | null> | null = null;

/**
 * Loads PostHog once. Two lanes (project123 D-002):
 *  - "base" → cookieless (persistence: memory, no PII / cross-session id), replay
 *             disabled. Runs WITHOUT consent: aggregate events + heatmaps only.
 *  - "full" → cookie persistence + session replay. Only after the visitor grants
 *             the "statistics" consent category (personal-data lane).
 */
async function ensurePosthog(mode: "base" | "full"): Promise<PostHogClient | null> {
  if (client) return client;
  if (!POSTHOG_KEY) {
    console.warn("[POSTHOG][init_010] VITE_POSTHOG_KEY not set — PostHog disabled");
    return null;
  }
  if (!loading) {
    loading = import("posthog-js")
      .then((mod) => {
        const ph = mod.default as unknown as PostHogClient;
        ph.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          capture_pageview: true,
          autocapture: true,
          disable_session_recording: true, // replay is consent-only (full lane)
          capture_exceptions: false, // error tracking is consent-gated too (project123 D-003)
          persistence: mode === "full" ? "cookie" : "memory", // base = cookieless
        });
        client = ph;
        return ph;
      })
      .catch((e) => {
        console.warn("[POSTHOG][init_020] failed to load posthog-js", e);
        loading = null;
        return null;
      });
  }
  return loading;
}

/**
 * Applies the current consent choice to PostHog (project100 D-005 / project123 D-002).
 * - Granted (statistics: true): ensure the FULL lane — cookie persistence, capturing
 *   on, session replay started (upgrades the base lane if it is already running).
 * - Explicitly refused (cookie present, statistics false): stop the full lane and opt
 *   out entirely — respect the refusal, including for the cookieless base lane.
 * - No argument + no cookie yet: nothing to upgrade; the BASE cookieless lane already
 *   runs (see startup below), so product/journey signal is never gated by the banner.
 */
export async function applyPosthogConsent(consent?: AnalyticsConsent | null): Promise<void> {
  const c = consent ?? readConsentCookie();
  // NO DECISION YET IS NOT A REFUSAL. The docstring eight lines above says exactly that — *"No argument
  // + no cookie yet: nothing to upgrade; the BASE cookieless lane already runs"* — and this branch used
  // to say the opposite, stopping the base lane and opting the visitor out. The guard makes the code
  // say what it means, and it stays for that reason.
  //
  // WHAT THIS IS NOT — corrected 2026-09-28, after the first version of this comment shipped a wrong
  // diagnosis alongside the fix. The doorway's empty funnel was NOT caused by this branch. That reading
  // came from `__ph_opt_in_out_<token>` reading `"1"` on a fresh visitor, taken as "opted out"; the
  // library's own source (`packages/browser/src/consent.ts`) says the opposite — `optInOut(isOptedIn)`
  // writes `isOptedIn ? 1 : 0`, so `"1"` means GRANTED. Measured on the live DEV doorway afterwards,
  // the journey's events leave the browser normally (`$opt_in`, `$pageview`, `ftue_entry`, `taste_done`
  // on one scan), and this branch is unreachable in practice: its callers pass either a real consent
  // choice or a cookie that already granted statistics. The flag is evidence of an opt-in, never of
  // silence — that is the mistake this comment used to record.
  if (!c) return;
  if (!c.statistics) {
    if (client) {
      client.stopSessionRecording();
      client.opt_out_capturing();
    }
    return;
  }
  const ph = await ensurePosthog("full");
  if (!ph) return;
  // project123 D-003: upgrade base → full — cookie persistence, replay, AND error
  // tracking (exceptions carry personal data, so they follow the consent lane).
  ph.set_config({ persistence: "cookie", capture_exceptions: true });
  ph.opt_in_capturing();
  ph.startSessionRecording();
}

/**
 * Links the signed-in user's stable Firebase UID to PostHog (project123). Full lane
 * ONLY: if the visitor hasn't granted "statistics", this is a no-op so the cookieless
 * base lane stays anonymous. Call after login / auth restore / register.
 */
export async function posthogIdentify(uid: string): Promise<void> {
  if (!readConsentCookie()?.statistics) return;
  const ph = await ensurePosthog("full");
  ph?.identify(uid);
}

/**
 * The PostHog distinct id, for ONE purpose: linking a stored feedback row back to the session
 * recording it came from (PROJECT-289 D-289-003).
 *
 * ⚠️ **THE CONSENTED LANE ONLY, AND THE GUARD IS THE POINT.** On the cookieless base lane there is no
 * stable id and there must not be one — a reference that survives a page view would turn an
 * anonymous answer into a trackable one, which is exactly what the base lane exists to prevent. So
 * this returns `undefined` unless "statistics" was granted, and the caller stores nothing.
 *
 * Returns `undefined` rather than `null`: the caller's field is optional, and "we have no reference"
 * and "the reference is empty" are the same fact here.
 */
export function posthogDistinctId(): string | undefined {
  if (!readConsentCookie()?.statistics) return undefined;
  return client?.get_distinct_id?.() || undefined;
}

/** Stops linking events to the previous user on logout (project123). */
export function posthogReset(): void {
  client?.reset();
}

/**
 * Captures a custom event (project123 D-002). Safe no-op when PostHog is disabled
 * (no VITE_POSTHOG_KEY) or not yet initialized, and after an explicit statistics
 * refusal (opt-out swallows the call). Events on the cookieless base lane stay
 * anonymous; the full lane links them to the identified user.
 */
export function posthogCapture(event: string, properties?: Record<string, unknown>): void {
  client?.capture(event, properties);
}

// Startup (project123 D-002 — two lanes):
//  - No consent decision yet → run the cookieless BASE lane so heatmaps/autocapture
//    work without the banner; replay stays off until consent.
//  - Returning visitor who already granted → full lane (replay + identity).
//  - Returning visitor who explicitly refused statistics → stay silent (respect it).
if (typeof window !== "undefined") {
  const existing = readConsentCookie();
  if (existing?.statistics) {
    void applyPosthogConsent(existing);
  } else if (!existing) {
    void (async () => {
      const ph = await ensurePosthog("base");
      ph?.opt_in_capturing();
    })();
  }
}
